"use client";

import React, { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { useGetSocialUser, useSocialPayMutations } from "@/hooks/social-pay";
import { BlockiesAvatar } from "@/components/blockies-avatar";
import {
  X,
  LogOut,
  Twitter,
  Link2,
  Unlink,
  Mail,
  MessageSquare,
  Copy,
  Check,
  Linkedin,
} from "lucide-react";
import Image from "next/image";
import { Button } from "@/components/ui/button";

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UserSettingsModal: React.FC<UserSettingsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { user, email, address, logout, linkEmail, unlinkEmail } = useAuth();
  const { data: socialUser } = useGetSocialUser();
  const {
    linkTwitter,
    unlinkTwitter,
    linkDiscord,
    unlinkDiscord,
    linkLinkedIn,
    unlinkLinkedIn,
  } = useSocialPayMutations();

  const [copiedAddress, setCopiedAddress] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleCopyAddress = async () => {
    if (address) {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    }
  };

  const handleLogout = async () => {
    setIsLoading(true);
    await logout();
    setIsLoading(false);
    onClose();
  };

  const formatAddress = (addr: string) => {
    if (!addr) return "";
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100]"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: "-50%", x: "-50%" }}
            animate={{ opacity: 1, scale: 1, y: "-50%", x: "-50%" }}
            exit={{ opacity: 0, scale: 0.95, y: "-50%", x: "-50%" }}
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="fixed top-1/2 left-1/2 w-full max-w-md bg-yiedl-dark border border-yiedl-gray-800 rounded-2xl z-[101] shadow-2xl mx-4"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-yiedl-gray-800">
              <div className="flex items-center gap-3">

                <h2 className="text-xl font-bold text-white">User Settings</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-yiedl-gray-800 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-yiedl-gray-400" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6">
              {/* Profile Section */}
              <div className="flex items-center gap-4">
                <BlockiesAvatar
                  seed={address || ""}
                  size={16}
                  scale={4}
                  className="border-2 border-yiedl-primary"
                />
                <div className="flex-1">
                  <p className="text-white font-semibold">
                    {email || "Anonymous User"}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-yiedl-gray-400 text-sm">
                      {formatAddress(address || "")}
                    </p>
                    <button
                      onClick={handleCopyAddress}
                      className="p-1 hover:bg-yiedl-gray-800 rounded transition-colors"
                    >
                      {copiedAddress ? (
                        <Check className="w-3 h-3 text-yiedl-primary" />
                      ) : (
                        <Copy className="w-3 h-3 text-yiedl-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Social Accounts */}
              <div className="space-y-3">
                <h3 className="text-yiedl-gray-400 text-sm font-bold uppercase tracking-wider">
                  Social Accounts
                </h3>

                <div className="space-y-2">
                  {/* Twitter */}
                  <div className="p-4 bg-yiedl-gray-900/50 rounded-xl border border-yiedl-gray-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Twitter className="w-5 h-5 text-yiedl-gray-400" />
                        <div>
                          <p className="text-white text-sm font-medium">
                            X (Twitter)
                          </p>
                          {(socialUser?.twitterMetadata ||
                            socialUser?.twitter) && (
                            <p className="text-yiedl-gray-400 text-xs">
                              @
                              {socialUser?.twitterMetadata?.username ||
                                socialUser?.twitter?.username}
                            </p>
                          )}
                        </div>
                      </div>
                      {socialUser?.twitterMetadata || socialUser?.twitter ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => unlinkTwitter.mutate()}
                          disabled={unlinkTwitter.isPending}
                          className="h-8 px-3 text-xs"
                        >
                          <Unlink className="w-3 h-3 mr-1" />
                          Unlink
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            linkTwitter.mutate({
                              redirectUrl: window.location.href,
                            })
                          }
                          disabled={linkTwitter.isPending}
                          className="h-8 px-3 text-xs"
                        >
                          <Link2 className="w-3 h-3 mr-1" />
                          Link
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Discord */}
                  <div className="p-4 bg-yiedl-gray-900/50 rounded-xl border border-yiedl-gray-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <MessageSquare className="w-5 h-5 text-yiedl-gray-400" />
                        <div>
                          <p className="text-white text-sm font-medium">
                            Discord
                          </p>
                          {(socialUser?.discordMetadata ||
                            socialUser?.discord) && (
                            <p className="text-yiedl-gray-400 text-xs">
                              @
                              {socialUser?.discordMetadata?.username ||
                                socialUser?.discord?.username}
                            </p>
                          )}
                        </div>
                      </div>
                      {socialUser?.discordMetadata || socialUser?.discord ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => unlinkDiscord.mutate()}
                          disabled={unlinkDiscord.isPending}
                          className="h-8 px-3 text-xs"
                        >
                          <Unlink className="w-3 h-3 mr-1" />
                          Unlink
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => linkDiscord.mutate()}
                          disabled={linkDiscord.isPending}
                          className="h-8 px-3 text-xs"
                        >
                          <Link2 className="w-3 h-3 mr-1" />
                          Link
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* LinkedIn */}
                  <div className="p-4 bg-yiedl-gray-900/50 rounded-xl border border-yiedl-gray-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Linkedin className="w-5 h-5 text-yiedl-gray-400" />
                        <div>
                          <p className="text-white text-sm font-medium">
                            LinkedIn
                          </p>
                          {(socialUser?.linkedInMetadata ||
                            socialUser?.linkedIn) && (
                            <p className="text-yiedl-gray-400 text-xs">
                              {socialUser?.linkedInMetadata?.name ||
                                socialUser?.linkedIn?.name}
                            </p>
                          )}
                        </div>
                      </div>
                      {socialUser?.linkedInMetadata || socialUser?.linkedIn ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => unlinkLinkedIn.mutate()}
                          disabled={unlinkLinkedIn.isPending}
                          className="h-8 px-3 text-xs"
                        >
                          <Unlink className="w-3 h-3 mr-1" />
                          Unlink
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => linkLinkedIn.mutate()}
                          disabled={linkLinkedIn.isPending}
                          className="h-8 px-3 text-xs"
                        >
                          <Link2 className="w-3 h-3 mr-1" />
                          Link
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Logout Button */}
              <Button
                variant="secondary"
                onClick={handleLogout}
                disabled={isLoading}
                className="w-full"
              >
                <LogOut className="w-5 h-5 mr-2" />
                {isLoading ? "Logging Out..." : "Logout"}
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return typeof window !== "undefined"
    ? createPortal(modalContent, document.body)
    : null;
};
